import type { UseQueryOptions } from '@tanstack/react-query'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  GenderResponseDataType,
  GetAllGenderResponseDataType,
  InfiniteQueryOptions
} from './types'

const GENDER_QUERY_KEY = (params: {
  page: number
  per_page: number
  search: string
}) => ['reference_gender', { ...params }] as const

type GenderQueryKey = ReturnType<typeof GENDER_QUERY_KEY>

const getBatch = (
  opts?: Partial<{
    page: number
    per_page: number
    search: string
  }>
): InfiniteQueryOptions<GenderQueryKey> => {
  const params = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 10,
    search: opts?.search ?? ''
  }

  return {
    queryKey: GENDER_QUERY_KEY(params),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }) => {
      try {
        const res = await api.get<GetAllGenderResponseDataType>(
          '/referensi/jenis-kelamin',
          {
            params: {
              page: pageParam,
              per_page: params.per_page,
              nama:
                params.search.length > 3
                  ? `%${params.search.toUpperCase()}%`
                  : undefined
            }
          }
        )

        return {
          message: res.data.message,
          data: res.data.data,
          meta: res.data.meta
        }
      } catch (ex) {
        throw handleAxiosError(ex)
      }
    }
  }
}

const GENDER_BY_ID_QUERY_KEY = (params: { id: string }) =>
  ['reference_gender', params] as const

const byId = (opts: {
  id: string
}): UseQueryOptions<GenderResponseDataType> => ({
  queryKey: GENDER_BY_ID_QUERY_KEY(opts),
  queryFn: async () => {
    try {
      const res = await api.get<GenderResponseDataType>(
        `/referensi/jenis-kelamin/${opts.id}`
      )

      const data = res.data.data
      if (!data) {
        throw new Error(`ID jenis kelamin ${opts.id} tidak dapat ditemukan.`)
      }

      return {
        message: res.data.message,
        data
      }
    } catch (ex) {
      throw handleAxiosError(ex)
    }
  }
})

export const genderReference = {
  queryKey: {
    all: GENDER_QUERY_KEY,
    byId: GENDER_BY_ID_QUERY_KEY
  },
  batched: getBatch,
  byId
}
